import React, { useState, useEffect } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import axios from 'axios';

function ReachVisualizer({ scenario, onUpdateScenario, onCalculateReach, loading }) {
  const [audiences, setAudiences] = useState([]);
  const [channels, setChannels] = useState([]);
  const [loadingData, setLoadingData] = useState(false);

  // Fetch available audiences and channels
  useEffect(() => {
    const fetchData = async () => {
      setLoadingData(true);
      try {
        // Fetch audiences
        const audiencesResponse = await axios.get(
          'https://n8n.iniai.cloud/webhook/available-audiences',
          {
            headers: {
              'x-api-key': 'auth_k_2f9a8b3c7d6e4f5g9h8i1j2k3l4m5n6',
              'Content-Type': 'application/json'
            }
          }
        );

        console.log("Raw audience response:", audiencesResponse.data);
        if (Array.isArray(audiencesResponse.data)) {
          setAudiences(audiencesResponse.data);
        } else {
          console.error("Unexpected audience response:", audiencesResponse.data);
        }

        // Fetch channels
        const channelsResponse = await axios.get(
          'https://n8n.iniai.cloud/webhook/available-channels',
          {
            headers: {
              'x-api-key': 'auth_k_2f9a8b3c7d6e4f5g9h8i1j2k3l4m5n6',
              'Content-Type': 'application/json'
            }
          }
        );

        console.log("Raw channel response:", channelsResponse.data);
        if (Array.isArray(channelsResponse.data)) {
          setChannels(channelsResponse.data);
        } else {
          console.error("Unexpected channels response:", channelsResponse.data);
        }
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoadingData(false);
      }
    };

    fetchData();
  }, []);

  // Debug state updates
  useEffect(() => console.log("Updated audiences state:", audiences), [audiences]);
  useEffect(() => console.log("Updated channels state:", channels), [channels]);

  // Handle audience selection
  const handleAudienceChange = (e) => {
    onUpdateScenario({ audienceName: e.target.value });
  };

  // Handle budget change
  const handleBudgetChange = (e) => {
    const budgetValue = parseInt(e.target.value, 10) || 0;
    onUpdateScenario({ ...scenario, budget: budgetValue });
  };

  // Handle channel selection
  const handleChannelSelection = (channel, allocation) => {
    const existingChannelIndex = scenario.channels.findIndex(c => c.name === channel["Channel Name"]);
    const updatedChannels = [...scenario.channels];

    if (existingChannelIndex >= 0) {
      updatedChannels[existingChannelIndex] = {
        ...updatedChannels[existingChannelIndex],
        budgetAllocation: allocation
      };
    } else {
      updatedChannels.push({
        name: channel["Channel Name"],
        platform: channel.platform || "Unknown",
        budgetAllocation: allocation
      });
    }

    onUpdateScenario({ ...scenario, channels: updatedChannels });
  };

  // Remove a channel from the scenario
  const removeChannel = (channelName) => {
    onUpdateScenario({
      ...scenario,
      channels: scenario.channels.filter(c => c.name !== channelName)
    });
  };

  return (
    <div className="reach-visualizer">
      <div className="scenario-header">
        <h2>
          <input
            type="text"
            value={scenario.name}
            onChange={(e) => onUpdateScenario({ ...scenario, name: e.target.value })}
            className="scenario-name-input"
          />
        </h2>

        <div className="scenario-actions">
          <button
            onClick={onCalculateReach}
            disabled={loading || !scenario.audienceName || scenario.channels.length === 0}
            className="calculate-btn"
          >
            {loading ? 'Calculating...' : 'Calculate Reach'}
          </button>
        </div>
      </div>

      <div className="scenario-config">
        <div className="config-section">
          <label>
            Audience:
            <select
              value={scenario.audienceName}
              onChange={handleAudienceChange}
              disabled={loadingData}
            >
              <option value="">Select an audience</option>
              {audiences.map(audience => (
                <option key={audience["Audience Name"]} value={audience["Audience Name"]}>
                  {audience["Audience Name"]}
                </option>
              ))}
            </select>
          </label>

          <label>
            Budget:
            <input
              type="number"
              value={scenario.budget}
              onChange={handleBudgetChange}
              min="0"
              step="1000"
            />
          </label>
        </div>

        <div className="channels-section">
          <h3>Channels</h3>

          {loadingData ? (
            <p>Loading available channels...</p>
          ) : (
            <div className="channel-selection">
              <div className="available-channels">
                <h4>Available Channels</h4>
                <ul>
                  {channels.map(channel => {
                    const isSelected = scenario.channels.some(c => c.name === channel["Channel Name"]);
                    return (
                      <li key={channel["Channel Name"]} className={isSelected ? 'selected' : ''}>
                        <span>{channel["Channel Name"]} ({channel.Reach || "No Reach Data"})</span>
                        {!isSelected && (
                          <button
                            onClick={() => handleChannelSelection(channel, 10)}
                            className="add-channel-btn"
                          >
                            Add
                          </button>
                        )}
                      </li>
                    );
                  })}
                </ul>
              </div>

              <div className="selected-channels">
                <h4>Selected Channels</h4>
                {scenario.channels.length === 0 ? (
                  <p>No channels selected</p>
                ) : (
                  <ul>
                    {scenario.channels.map(channel => (
                      <li key={channel.name}>
                        <div className="channel-details">
                          <span>{channel.name}</span>
                          <button
                            onClick={() => removeChannel(channel.name)}
                            className="remove-channel-btn"
                          >
                            Remove
                          </button>
                        </div>
                        <div className="allocation-slider">
                          <label>
                            Budget Allocation: {channel.budgetAllocation}%
                            <input
                              type="range"
                              min="1"
                              max="100"
                              value={channel.budgetAllocation}
                              onChange={(e) => handleChannelSelection(
                                channel,
                                parseInt(e.target.value, 10)
                              )}
                            />
                          </label>
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default ReachVisualizer;
